import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import FormWithValidation from "../../components/common/FormWithValidation";
import Loader from "../../components/common/Loader";
import { createRequirement, getRequirement, updateRequirement } from "../../api/requirements";
import { useToast } from "../../components/common/ToastProvider";

const schema = z.object({
  code: z.string().regex(/^[A-Z0-9._-]+$/, "Code must match pattern A-Z0-9._-"),
  title: z.string().min(1, "Title is required"),
  description: z.string().optional().nullable(),
  display_order: z.number().int().min(0, "Display order must be >= 0"),
});

export default function RequirementForm() {
  const { fid, rid } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const isEdit = Boolean(rid);

  const [serverFieldErrors, setServerFieldErrors] = useState({});

  const { data, isLoading } = useQuery({
    queryKey: ["requirement", rid],
    queryFn: () => getRequirement(rid),
    enabled: isEdit,
  });

  const mutation = useMutation({
    mutationFn: (payload) =>
      isEdit ? updateRequirement(fid, rid, payload) : createRequirement(fid, payload),
    onSuccess: (result) => {
      success(isEdit ? "Requirement updated" : "Requirement created");
      queryClient.invalidateQueries({ queryKey: ["requirements"] });
      queryClient.invalidateQueries({ queryKey: ["requirement", result.id] });
      navigate(`/requirements/${result.id}`);
    },
    onError: (err) => {
      setServerFieldErrors(err?.fieldErrors || {});
      showError(err?.detail || "Failed to save requirement");
    },
  });

  const defaultValues = useMemo(
    () => ({
      code: data?.code || "",
      title: data?.title || "",
      description: data?.description || "",
      display_order: data?.display_order ?? 0,
    }),
    [data]
  );

  const breadcrumbItems = [
    { label: "Frameworks", to: "/frameworks" },
    fid ? { label: "Framework", to: `/frameworks/${fid}` } : null,
    { label: isEdit ? "Edit Requirement" : "New Requirement" },
  ].filter(Boolean);

  return (
    <div>
      <Breadcrumbs items={breadcrumbItems} />
      <h1>{isEdit ? "Edit Requirement" : "New Requirement"}</h1>
      {isEdit && isLoading && <Loader />}
      <FormWithValidation
        schema={schema}
        defaultValues={defaultValues}
        onSubmit={(values) => mutation.mutate({ ...values, display_order: Number(values.display_order) })}
        onCancel={() => navigate(fid ? `/frameworks/${fid}` : "/frameworks")}
        serverFieldErrors={serverFieldErrors}
        submitLabel={isEdit ? "Save" : "Create"}
      >
        {({ register, formState }) => (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxWidth: "400px" }}>
            <label>
              Code
              <input data-testid="requirement-code" {...register("code")} />
              {formState.errors.code && <span style={{ color: "red" }}>{formState.errors.code.message}</span>}
            </label>
            <label>
              Title
              <input data-testid="requirement-title" {...register("title")} />
              {formState.errors.title && <span style={{ color: "red" }}>{formState.errors.title.message}</span>}
            </label>
            <label>
              Description
              <textarea data-testid="requirement-description" {...register("description")} />
              {formState.errors.description && (
                <span style={{ color: "red" }}>{formState.errors.description.message}</span>
              )}
            </label>
            <label>
              Display order
              <input
                type="number"
                data-testid="requirement-display-order"
                {...register("display_order", { valueAsNumber: true })}
              />
              {formState.errors.display_order && (
                <span style={{ color: "red" }}>{formState.errors.display_order.message}</span>
              )}
            </label>
          </div>
        )}
      </FormWithValidation>
    </div>
  );
}
